/**
 * Leviathan - Activities List Page
 * Quasar Prime LLC
 *
 * CRM Activities management with Log Call and Schedule Follow-up modals
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Phone,
  Mail,
  Calendar,
  CheckSquare,
  FileText,
  MapPin,
  Send,
  Clock,
  Search,
  Filter,
  Loader2,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
} from 'lucide-react';
import { API_URL } from '@/config/api';

const ACTIVITY_TYPES = [
  { value: 'CALL', label: 'Call', icon: Phone, color: 'text-blue-400' },
  { value: 'EMAIL', label: 'Email', icon: Mail, color: 'text-green-400' },
  { value: 'MEETING', label: 'Meeting', icon: Calendar, color: 'text-purple-400' },
  { value: 'TASK', label: 'Task', icon: CheckSquare, color: 'text-orange-400' },
  { value: 'NOTE', label: 'Note', icon: FileText, color: 'text-slate-400' },
  { value: 'SITE_VISIT', label: 'Site Visit', icon: MapPin, color: 'text-cyan-400' },
  { value: 'QUOTE_SENT', label: 'Quote Sent', icon: Send, color: 'text-amber-400' },
  { value: 'FOLLOW_UP', label: 'Follow-up', icon: Clock, color: 'text-pink-400' },
];

const ACTIVITY_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-500/20 text-green-400' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-slate-500/20 text-slate-400' },
];

interface Activity {
  id: string;
  type: string;
  subject: string;
  description: string | null;
  status: string;
  scheduledDate: string | null;
  completedDate: string | null;
  durationMinutes: number | null;
  outcome: string | null;
  nextSteps: string | null;
  leadId: string | null;
  customerId: string | null;
  assignedToId: string | null;
  createdById: string;
  createdAt: string;
  lead?: { id: string; leadNumber: string; companyName: string } | null;
  customer?: { id: string; customerNumber: string; name: string } | null;
  assignedTo?: { id: string; firstName: string | null; lastName: string | null } | null;
  createdBy?: { id: string; firstName: string | null; lastName: string | null } | null;
}

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface Lead {
  id: string;
  leadNumber: string;
  companyName: string;
}

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
}

export default function ActivitiesList() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showLogCallModal, setShowLogCallModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);

  // Fetch activities
  const { data: activitiesData, isLoading } = useQuery({
    queryKey: ['activities', { search, type: typeFilter, status: statusFilter, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', page.toString());
      params.set('pageSize', '25');

      const res = await fetch(`${API_URL}/api/activities?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      return res.json();
    },
  });

  // Fetch users for assignment
  const { data: users } = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Fetch leads for linking
  const { data: leads } = useQuery<Lead[]>({
    queryKey: ['leads-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads?pageSize=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch leads');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Fetch customers for linking
  const { data: customers } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/customers?pageSize=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Complete activity mutation
  const completeMutation = useMutation({
    mutationFn: async ({ id, outcome }: { id: string; outcome?: string }) => {
      const res = await fetch(`${API_URL}/api/activities/${id}/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ outcome }),
      });
      if (!res.ok) throw new Error('Failed to complete activity');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  // Cancel activity mutation
  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/activities/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to cancel activity');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  const activities = activitiesData?.data || [];
  const totalPages = activitiesData?.pagination?.totalPages || 1;
  const total = activitiesData?.pagination?.total || 0;

  const getActivityIcon = (type: string) => {
    const activityType = ACTIVITY_TYPES.find((t) => t.value === type);
    if (!activityType) return FileText;
    return activityType.icon;
  };

  const getActivityColor = (type: string) => {
    const activityType = ACTIVITY_TYPES.find((t) => t.value === type);
    return activityType?.color || 'text-slate-400';
  };

  const getStatusBadge = (status: string) => {
    const statusInfo = ACTIVITY_STATUSES.find((s) => s.value === status);
    return statusInfo?.color || 'bg-slate-500/20 text-slate-400';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Activities</h1>
          <p className="text-slate-400">Track all CRM activities and interactions</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowLogCallModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            <Phone className="w-4 h-4" />
            Log Call
          </button>
          <button
            onClick={() => setShowFollowUpModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            <Clock className="w-4 h-4" />
            Schedule Follow-up
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search activities..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            showFilters || typeFilter || statusFilter
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
              : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-slate-200'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {(typeFilter || statusFilter) && (
            <span className="w-5 h-5 rounded-full bg-amber-500 text-slate-900 text-xs flex items-center justify-center">
              {(typeFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-400 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">All Types</option>
              {ACTIVITY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">All Statuses</option>
              {ACTIVITY_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setTypeFilter('');
                setStatusFilter('');
                setPage(1);
              }}
              className="px-4 py-2 text-slate-400 hover:text-slate-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-slate-400">
        Showing {activities.length} of {total} activities
      </div>

      {/* Activities List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : activities.length === 0 ? (
        <div className="bg-slate-800/50 rounded-xl p-12 text-center border border-slate-700/50">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No activities found</h3>
          <p className="text-slate-500 mb-6">
            {search || typeFilter || statusFilter
              ? 'Try adjusting your filters'
              : 'Start by logging a call or scheduling a follow-up'}
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setShowLogCallModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              Log Call
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Activity
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Related To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Scheduled
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Assigned To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {activities.map((activity: Activity) => {
                const ActivityIcon = getActivityIcon(activity.type);
                return (
                  <tr key={activity.id} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-slate-800 ${getActivityColor(activity.type)}`}>
                          <ActivityIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-100">{activity.subject}</div>
                          <div className="text-sm text-slate-400">
                            {ACTIVITY_TYPES.find((t) => t.value === activity.type)?.label}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {activity.lead ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm text-slate-100">{activity.lead.companyName}</div>
                            <div className="text-xs text-slate-500">{activity.lead.leadNumber}</div>
                          </div>
                        </div>
                      ) : activity.customer ? (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm text-slate-100">{activity.customer.name}</div>
                            <div className="text-xs text-slate-500">{activity.customer.customerNumber}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(activity.status)}`}>
                        {activity.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {formatDate(activity.scheduledDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {activity.assignedTo
                        ? `${activity.assignedTo.firstName || ''} ${activity.assignedTo.lastName || ''}`
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {activity.status === 'PENDING' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => completeMutation.mutate({ id: activity.id })}
                            className="p-1.5 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
                            title="Complete"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => cancelMutation.mutate(activity.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Log Call Modal */}
      {showLogCallModal && (
        <LogCallModal
          onClose={() => setShowLogCallModal(false)}
          leads={leads || []}
          customers={customers || []}
          accessToken={accessToken || ''}
        />
      )}

      {/* Schedule Follow-up Modal */}
      {showFollowUpModal && (
        <ScheduleFollowUpModal
          onClose={() => setShowFollowUpModal(false)}
          leads={leads || []}
          customers={customers || []}
          users={users || []}
          accessToken={accessToken || ''}
        />
      )}
    </div>
  );
}

// Log Call Modal Component
function LogCallModal({
  onClose,
  leads,
  customers,
  accessToken,
}: {
  onClose: () => void;
  leads: Lead[];
  customers: Customer[];
  accessToken: string;
}) {
  const queryClient = useQueryClient();
  const [entityType, setEntityType] = useState<'lead' | 'customer'>('lead');
  const [formData, setFormData] = useState({
    entityId: '',
    subject: '',
    description: '',
    outcome: '',
    durationMinutes: '',
    nextSteps: '',
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await fetch(`${API_URL}/api/activities`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create activity');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, unknown> = {
      type: 'CALL',
      subject: formData.subject || 'Phone Call',
      description: formData.description || undefined,
      outcome: formData.outcome || undefined,
      durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes, 10) : undefined,
      nextSteps: formData.nextSteps || undefined,
      status: 'COMPLETED',
      completedDate: new Date(),
    };

    if (entityType === 'lead') {
      data.leadId = formData.entityId;
    } else {
      data.customerId = formData.entityId;
    }

    createMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
              <Phone className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-slate-100">Log Call</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Entity Type Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setEntityType('lead');
                setFormData({ ...formData, entityId: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                entityType === 'lead'
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Lead
            </button>
            <button
              type="button"
              onClick={() => {
                setEntityType('customer');
                setFormData({ ...formData, entityId: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                entityType === 'customer'
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Customer
            </button>
          </div>

          {/* Entity Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              {entityType === 'lead' ? 'Select Lead' : 'Select Customer'} <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.entityId}
              onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
              required
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">Select...</option>
              {entityType === 'lead'
                ? leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.companyName} ({lead.leadNumber})
                    </option>
                  ))
                : customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.customerNumber})
                    </option>
                  ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Subject</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g., Introductory call"
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="What was discussed..."
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          {/* Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Duration (min)</label>
              <input
                type="number"
                value={formData.durationMinutes}
                onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Outcome</label>
              <select
                value={formData.outcome}
                onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select...</option>
                <option value="Connected">Connected</option>
                <option value="Left Voicemail">Left Voicemail</option>
                <option value="No Answer">No Answer</option>
                <option value="Wrong Number">Wrong Number</option>
                <option value="Busy">Busy</option>
              </select>
            </div>
          </div>

          {/* Next Steps */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Next Steps</label>
            <input
              type="text"
              value={formData.nextSteps}
              onChange={(e) => setFormData({ ...formData, nextSteps: e.target.value })}
              placeholder="e.g., Send proposal"
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.entityId || createMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Log Call
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Schedule Follow-up Modal Component
function ScheduleFollowUpModal({
  onClose,
  leads,
  customers,
  users,
  accessToken,
}: {
  onClose: () => void;
  leads: Lead[];
  customers: Customer[];
  users: User[];
  accessToken: string;
}) {
  const queryClient = useQueryClient();
  const [entityType, setEntityType] = useState<'lead' | 'customer'>('lead');
  const [formData, setFormData] = useState({
    entityId: '',
    subject: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    assignedToId: '',
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await fetch(`${API_URL}/api/activities/follow-up`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to schedule follow-up');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime || '09:00'}`);

    const data: Record<string, unknown> = {
      subject: formData.subject,
      description: formData.description || undefined,
      scheduledDate: scheduledDateTime,
      assignedToId: formData.assignedToId || undefined,
    };

    if (entityType === 'lead') {
      data.leadId = formData.entityId;
    } else {
      data.customerId = formData.entityId;
    }

    createMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
              <Clock className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-slate-100">Schedule Follow-up</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Entity Type Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setEntityType('lead');
                setFormData({ ...formData, entityId: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                entityType === 'lead'
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Lead
            </button>
            <button
              type="button"
              onClick={() => {
                setEntityType('customer');
                setFormData({ ...formData, entityId: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                entityType === 'customer'
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Customer
            </button>
          </div>

          {/* Entity Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              {entityType === 'lead' ? 'Select Lead' : 'Select Customer'} <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.entityId}
              onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
              required
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">Select...</option>
              {entityType === 'lead'
                ? leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.companyName} ({lead.leadNumber})
                    </option>
                  ))
                : customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.customerNumber})
                    </option>
                  ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Subject <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              placeholder="e.g., Follow up on proposal"
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Additional details..."
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                required
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Time</label>
              <input
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Assign To</label>
            <select
              value={formData.assignedToId}
              onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">Myself</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.entityId || !formData.subject || !formData.scheduledDate || createMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  Schedule
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
